/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
// Building blocks shared by the customers list, its dialogs and the profile.
// Same surface tokens as the dashboard (rounded-[14px] cards on gray-200
// borders, 44px controls, indigo accent).
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../../contexts/LanguageContext";
import { SEGMENT_COLORS, segmentLabelKey } from "../dashboard/segments";

export function Pill({ className = "bg-gray-100 text-gray-800", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </span>
  );
}

const SEGMENT_PILLS = {
  active: "bg-indigo-50 text-indigo-800",
  cooling: "bg-indigo-50 text-indigo-700",
  at_risk: "bg-orange-50 text-orange-800",
  lost: "bg-gray-100 text-gray-700",
  never: "bg-gray-100 text-gray-700",
};

export function SegmentPill({ segment }) {
  const { t } = useLanguage();
  return <Pill className={SEGMENT_PILLS[segment]}>{t(segmentLabelKey(segment))}</Pill>;
}

export function SegmentDot({ segment, className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-2 w-2 shrink-0 rounded-full ring-1 ring-gray-200 ${className}`}
      style={{ background: SEGMENT_COLORS[segment] }}
    />
  );
}

// "+1" on indigo for credits, orange for debits, gray for zero.
export function DeltaPill({ value, children }) {
  const tone =
    value > 0
      ? "bg-indigo-50 text-indigo-800"
      : value < 0
        ? "bg-orange-50 text-orange-800"
        : "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap tabular-nums ${tone}`}
    >
      {children}
    </span>
  );
}

export function CloseButton({ onClick, label, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 ${className}`}
    >
      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

// Centered dialog. `role` is "dialog" or "alertdialog".
// `closeLabel` adds a round close button to the header.
export function Modal({ open, onClose, title, icon, children, footer, size = "max-w-[460px]", role = "dialog", closeLabel }) {
  return (
    <Dialog open={open} onClose={onClose} role={role} className="relative z-50">
      <div className="fixed inset-0 bg-gray-900/45" aria-hidden="true" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-start justify-center px-4 py-16 sm:py-24">
          <DialogPanel
            className={`flex w-full ${size} flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl`}
          >
            <div className="flex items-center gap-3">
              {icon}
              <DialogTitle as="h2" className="min-w-0 flex-1 text-[19px] font-semibold text-gray-900">
                {title}
              </DialogTitle>
              {closeLabel && <CloseButton onClick={() => onClose(false)} label={closeLabel} />}
            </div>
            {children}
            {footer && <div className="flex flex-wrap justify-end gap-2">{footer}</div>}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
